import React, {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useParams} from 'react-router-dom';
import {ClipLoader} from 'react-spinners';
import {FollowJobs, FollowQualifications, FollowSkills} from './components/FollowItems/main';
import ButtonCircle from '../../components/Common/ButtonCircle';
import {RadarChart} from '../../components/CommonComponents';
import {ExploreSoftSkills, RmInfo} from '../../components/CompanyComponents';
import {
    selectSelectedCompany,
    selectSelectedRm,
    storeSelectedCompany,
    storeSelectedRm
} from '../../slices/selected';
import {getCompanyById, getRm} from '../../api/company';
import {selectCv, selectUser} from '../../slices/profile';
import {
    computeGameDeadline,
    getDoneTotalMandatorySkills,
    getDoneTotalNotOptionalJobs,
    getDoneTotalNotOptionalQualifications,
    getDoneTotalOptionalJobs,
    getDoneTotalOptionalQualifications,
    getDoneTotalOptionalSkills
} from '../../helpers/misc';
import {selectUid} from '../../slices/auth';
import {closeDialog, openDialog} from '../../slices/dialog';
import {LeaveRm} from '../../components/Dialog/LeaveRm';
import {getRmGamesByTarget} from '../../api/game';
import SectionNavbar from '../../components/SectionNavbar';
import {FollowTabButton} from './components/FollowTabButton';
import {completionPercentage} from '../../helpers/companyRm';
import InfoIcon from "@material-ui/icons/Info";
import {FollowGameBrief} from './components/FollowGameBrief';
import {useTranslation} from 'react-i18next';
import {Cookies} from "react-cookie-consent";
import {ROUTES} from '../../db/models/const';
import {getContentsByRmId} from '../../api/content';
import {Steps} from 'intro.js-react';
import 'intro.js/introjs.css';
import {whenStudentFollowedRmTimestamp} from '../../helpers/student';
import {Button, Col, Row} from "react-bootstrap";
import {MessageToFollowers} from "../../components/Dialog/MessageToFollowers";

var emojiCompact = require('emoji.json/emoji-compact.json');

const StudentFollow = () => {
    const {rmId} = useParams();
    const rm = useSelector(selectSelectedRm);
    const dispatch = useDispatch();
    const cv = useSelector(selectCv);
    const uid = useSelector(selectUid);
    const softSkills = cv?.softSkills;
    const user = useSelector(selectUser);
    const company = useSelector(selectSelectedCompany);
    const history = useHistory();
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [video, setVideo] = useState();
    const [stepsEnabled, setStepsEnabled] = useState(false);

    const [qualCounters, setQualCounters] = useState({
        mandatory: {done: 0, total: 0},
        optional: {done: 0, total: 0}
    });
    const [jobCounters, setJobCounters] = useState({
        mandatory: {done: 0, total: 0},
        optional: {done: 0, total: 0}
    });
    const [skillCounters, setSkillCounters] = useState({
        mandatory: {done: 0, total: 0},
        optional: {done: 0, total: 0}
    });
    const [percentage, setPercentage] = useState({
        mandatory: 0,
        optional: 0
    });

    const [games, setGames] = useState([]);
    const [itemToShow, setItemToShow] = useState("games");
    const {t} = useTranslation();

    const onUnfollow = () => {
        dispatch(openDialog({
            Content: LeaveRm,
            title: t("student_leaveRm_title"),
            props: {},
            closable: false
        }))
    };

    useEffect(() => {
        if (!rmId) {
            history.push(ROUTES.ME);
            return;
        }

        getRm(rmId).then(rm => {
            dispatch(storeSelectedRm({rm}))
        })
    }, [rmId])

    useEffect(() => {
        if (!rmId) return;

        getContentsByRmId(rmId).then(contents => {
            setVideo(contents?.[0]);
        });
    }, [rmId])

    useEffect(() => {
        if (!cv || !rm || !user) return;
        const qual = {
            optional: getDoneTotalOptionalQualifications(rm, cv),
            mandatory: getDoneTotalNotOptionalQualifications(rm, cv)
        };
        setQualCounters(qual);

        const job = {
            optional: getDoneTotalOptionalJobs(rm, cv),
            mandatory: getDoneTotalNotOptionalJobs(rm, cv)
        };
        setJobCounters(job);

        const skill = {
            optional: getDoneTotalOptionalSkills(rm, cv),
            mandatory: getDoneTotalMandatorySkills(rm, cv)
        };
        setSkillCounters(skill);

        //compute completion percentage of mandatory and optional rm items and games
        const percentage = completionPercentage(rm, cv, user, games);
        setPercentage(percentage);

        setItemToShow("games");
        // setGames(null);
    }, [cv, rm, games, user])

    const onSoftExploreClick = () => {
        dispatch(openDialog({
            Content: ExploreSoftSkills,
            title: "Soft Competencies",
            props: {
                softSkillsExplanation: rm.softSkillsExplanation,
            },
            closable: true
        }));
    }

    const goToCompany = () =>{
        history.push(ROUTES.COMPANY.replace(":companyId", company.id));
    }

    useEffect(() => {
        if (!rm) return;

        // reset lists of games and boolean that check if games have been downloaded
        setGames([]);
        setItemToShow("games");

        // if rm company not equal to previous one, get his logo
        if (company?.uid === rm?.companyId)
            return;

        getCompanyById(rm?.companyId)
            .then(company => {
                if (company) dispatch(storeSelectedCompany({company}));
            });
    }, [rm]);

    useEffect(() => {
        if (!rm || !user) return;

        if (itemToShow === "games") {
            const rmGroups = Object.values(rm?.groups || {});
            const groupIds = [...(rmGroups.filter(group => group.studentIds.find(sId => sId === uid)).map(group => group.id)), ""];
            getRmGamesByTarget(rm.id, groupIds).then(games => {
                const sortedGames = games.sort((gameA, gameB) => {
                    const deadlineA = computeGameDeadline(gameA, whenStudentFollowedRmTimestamp(user, gameA.rmId));
                    const deadlineB = computeGameDeadline(gameB, whenStudentFollowedRmTimestamp(user, gameB.rmId));

                    if (!deadlineA) return -1;
                    else if (!deadlineB) return 1;
                    else {
                        if (deadlineA && deadlineB && deadlineA < deadlineB) return 1;
                        if (deadlineA && deadlineB && deadlineA > deadlineB) return -1;
                        else return 1;
                    }
                });
                setGames(sortedGames);
                // setActiveAttendedGames(activeGames.filter(g => studentGames.map(sg => sg.id).includes(g.id)));
                // setGamesDownloaded(true);
            });
        }
    }, [rm, user, itemToShow]);

    // listen for window width changes to update column with done/undone cv items
    useEffect(() => {
        const handleWidth = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleWidth);
        return () => window.removeEventListener('resize', handleWidth);
    }, []);
    
    // show tutorial on tab change if is first visit
    useEffect(() => {
        if(!itemToShow) return;

        const tutorialDone = Cookies.get(`followTutorialDone_${itemToShow}`);
        if(!tutorialDone){
            dispatch(openDialog({
                Content: TutorialDialog,
                props: {
                    title: t(`student_follow_tutorial_${itemToShow}_title`),
                    text: t(`student_follow_tutorial_${itemToShow}_text`),
                    buttonText: t(`student_follow_tutorial_${itemToShow}_buttonText`),
                    item: itemToShow
                },
                closable: false,
            }))
        }

    }, [itemToShow])

    return (
        <div className="studentFollow">
            {rm && (
                <>
                    <Button className={"mb-2 fs-2"} variant="link" onClick={goToCompany}>{company?.name}</Button>
                    <div className="studentFollow__topElems">
                        <div className="studentFollow__rmInfo card">
                            <RmInfo rm={rm} logoUrl={company?.logoUrl} unfollow={onUnfollow} video={video}/>
                        </div>
                        <div className="studentFollow__radar card">
                            <div className="buttonAbsolute">
                                <ButtonCircle onClick={() => onSoftExploreClick()}><InfoIcon/></ButtonCircle>
                            </div>
                            <RadarChart softSkills={softSkills} rmSoftSkills={rm?.softSkills}/>
                        </div>
                    </div>
                    <h3 className={"mt-5 mb-3 fs-3 fw-bold show-mobile"}>{t("student_follow_progress_heading")}</h3>
                    <div className="studentFollow__completion card">
                        <div className="topContainer">
                            <p>{percentage.optional + "%"}</p>
                            <div className="top" style={{
                                height: windowWidth > 768 ? `${percentage.optional}%` : "100%",
                                width: windowWidth > 768 ? "100%" : `${percentage.optional}%`
                            }}></div>
                        </div>
                        <div className="bottomContainer">
                            <p>{percentage.mandatory + "%"}</p>
                            <div className="bottom" style={{
                                height: windowWidth > 768 ? `${percentage.mandatory}%` : "100%",
                                width: windowWidth > 768 ? "100%" : `${percentage.mandatory}%`
                            }}></div>
                        </div>
                    </div>

                    <h3 className={"mt-5 mb-3 fs-3 fw-bold"}>{t("student_follow_tabs_heading")}</h3>
                    <Row className="mb-4 gap-0">
                        <Col xs={3} className="p-1">
                            <FollowTabButton
                                title={t("student_follow_games")}
                                emoji={emojiCompact[3685]}
                                active={itemToShow === "games"}
                                onPress={() => setItemToShow("games")}
                            />
                        </Col>
                        <Col xs={3} className="p-1">
                            <FollowTabButton
                                title={t("student_follow_qual")}
                                emoji={emojiCompact[3741]}
                                active={itemToShow === "qualifications"}
                                onPress={() => setItemToShow("qualifications")}
                            />
                        </Col>
                        <Col xs={3} className="p-1">
                            <FollowTabButton
                                title={t("student_follow_job")}
                                emoji={emojiCompact[3894]}
                                active={itemToShow === "jobs"}
                                onPress={() => setItemToShow("jobs")}
                            />
                        </Col>
                        <Col xs={3} className="p-1">
                            <FollowTabButton
                                title={t("student_follow_skill")}
                                emoji={emojiCompact[3938]}
                                active={itemToShow === "skills"}
                                onPress={() => setItemToShow("skills")}
                            />
                        </Col>
                    </Row>
                    {itemToShow === "qualifications" && <FollowQualifications sectionCounters={qualCounters}/>}
                    {itemToShow === "jobs" && <FollowJobs sectionCounters={jobCounters}/>}
                    {itemToShow === "skills" && <FollowSkills sectionCounters={skillCounters}/>}
                    {itemToShow === "games" &&
                        <div className="studentFollow__games">
                            {games && games.map((game) => (
                                <FollowGameBrief key={game.id} game={game}/>
                            ))}
                        </div>
                    }
                </>
            )}
            {!rm && <ClipLoader size={50} color={"#7400b8"} loading={true}/>}
        </div>
    );
};

export default StudentFollow;

export const TutorialDialog = ({title, text, buttonText, item}) => {
    const dispatch = useDispatch();

    const onClose =()=>{
        Cookies.set(`followTutorialDone_${item}`, true, {expires: 1000});
        dispatch(closeDialog());
    }

    return (
        <div>
            <h3 className="fs-1 fw-bolder mb-2">{title}</h3>
            <p className="fs-3 mb-5">{text}</p>
            <Button onClick={onClose} className="rounded-borders w-100">{buttonText}</Button>
        </div>
    )
}